import { Box } from '@chakra-ui/react';
import type React from 'react';
import FaceliftOverlayLoader from '@/components/Atoms/FaceliftOverlayLoader';
import FaceliftText from '@/components/Atoms/FaceliftText';
import FaceliftDragDrop from '@/components/Molecules/FaceliftDragDrop';
import { FilePreviewList } from '@/components/Molecules/FilePreviewList';
import Show from '@/components/Molecules/Show';
import { type UploadedFile, useFileUpload } from '@/hooks';

export interface UploadDocumentsData {
	uploadedFiles: UploadedFile[];
}

export interface UploadDocumentsProps {
	data: UploadDocumentsData;
	errors?: Record<string, string>;
	onChange: (field: string, value: UploadDocumentsData) => void;
	clinicId: string;
	clinicName: string;
	folderName?: string;
}

const UploadDocuments: React.FC<UploadDocumentsProps> = ({
	data,
	errors,
	onChange,
	clinicId,
	clinicName,
	folderName,
}) => {
	// Extract clinic ID from different formats:
	// - "label_value" format (from Documents page): extract value part after underscore
	// - Just "value" format (from BV form): use as-is
	// Always remove dashes from the final clinic ID
	const extractClinicId = (id: string): string => {
		if (!id) return '61aea445e3b4ea11a816000d3a191041'; // Default fallback
		
		let clinicId = id;
		
		// Check if it's in "label_value" format (contains underscore)
		if (id.includes('_')) {
			// Split by underscore and take the last part (the clinic ID)
			const parts = id.split('_');
			clinicId = parts[parts.length - 1] || '61aea445e3b4ea11a816000d3a191041';
		}
		
		// Always remove dashes from the clinic ID (SharePoint doesn't use them)
		return clinicId.replaceAll('-', '');
	};

	const {
		handleFileUpload,
		handleDeleteFile,
		formatFileSize,
		uploadError,
		isUploading,
		isDeleting,
		isDisabled
	} = useFileUpload({
		files: data.uploadedFiles,
		onFilesChange: (files: UploadedFile[]) =>
			onChange('uploadedFiles', { uploadedFiles: files }),
		clinic: extractClinicId(clinicId), //remove default id when bv is integrated
		clinicName: clinicName,
		folderName: `Client Inbox${folderName ? `/${folderName}` : ''}`,
		disabled: !clinicId, // Disable if clinicId is not provided
	});

	return (
		<Box>
			{/* Overlay Loader while uploading */}
			<Show when={isUploading || isDeleting}>
				<FaceliftOverlayLoader />
			</Show>

			<FaceliftText
				fontSize={{ base: 'sm', md: 'md' }}
				fontWeight="semibold"
				mb={{ base: 4, md: 6 }}
				fontStyle="italic"
			>
				Upload Documents
			</FaceliftText>

			{/* Drag Drop Component */}
			<FaceliftDragDrop
				onFilesUpload={handleFileUpload}
				error={uploadError || errors?.['uploadDocuments.uploadedFiles']}
				multiple
				maxSize={104857600}
				disabled={isDisabled}
				disabledMessage="Please select a clinic before uploading files"
			/>

			{/* Uploaded Files List */}
			<FilePreviewList
				files={data.uploadedFiles}
				onDelete={handleDeleteFile}
				formatFileSize={formatFileSize}
			/>
		</Box>
	);
};

export default UploadDocuments;
